import React, { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';
import { Group, TextInput, PasswordInput, Button, Stack, Anchor, Text, Alert, Divider, Flex } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { SocialAuth } from "./SocialAuth";
import { useLocation, useNavigate } from 'react-router-dom';
import { signIn } from "../../api/Auth";
import logger from '../../utils/logger';

interface LoginProps {
    onLoginSuccess: () => void;
    onRegisterClick: () => void;
    onForgotPasswordClick: () => void;
}

interface NotificationState {
    type: 'success' | 'error' | 'info' | 'warning';
    title: string;
    message: string;
}

const Login: React.FC<LoginProps> = ({ 
    onLoginSuccess,
    onRegisterClick,
    onForgotPasswordClick
}) => {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [notification, setNotification] = useState<NotificationState | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();


    const form = useForm({
        initialValues: {
            email: '',
            password: '',
        },
        validate: {
            email: (val) => (/^\S+@\S+$/.test(val) ? null : 'Invalid email'),
            password: (val) => (val.length <= 6 ? 'Password should include at least 6 characters' : null),
        },
    });

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const email = searchParams.get('email');
        
        if (searchParams.get('verified') === 'true') {
            setNotification({
                type: 'success',
                title: 'Email Verified!',
                message: searchParams.get('message') || 'Your account has been verified successfully! Please sign in to continue.'
            });
        } else if (searchParams.get('exists') === 'true') {
            setNotification({
                type: 'warning',
                title: 'Account Exists',
                message: searchParams.get('message') || 'An account with this email already exists. Please sign in instead.'
            });
        } else if (searchParams.get('error')) {
            setNotification({
                type: 'error',
                title: 'Error',
                message: searchParams.get('message') || 'An error occurred. Please try again.'
            });
        } else if (searchParams.get('passwordReset') === 'true') {
            setNotification({
                type: 'success',
                title: 'Password Reset',
                message: 'Your password has been reset successfully. Please sign in with your new password.'
            });
        }

        if (email) {
            form.setFieldValue('email', email);
        }
    }, [location.search]);

    const handleEmailSignIn = async (email: string, password: string) => {
        setSubmitting(true);
        try {
            const result = await signIn(email, password);
            if (result.success) {
                if (result.error === 'forcePasswordChange') {
                    navigate('/reset-password?forced=true');
                } else {
                    onLoginSuccess();
                }
            } else {
                setErrorMessage(result.error || 'Authentication failed');
                form.setFieldValue('password', '');
            }
        } catch (error) {
            logger.error('Login error:', error);
        } finally {
            setSubmitting(false);
        }

    };

    const getNotificationColor = (type: string): string => {
        switch (type) {
            case 'success':
                return 'green';
            case 'error':
                return 'red';
            case 'warning':
                return 'yellow';
            case 'info':
                return 'blue';
            default:
                return 'gray';
        }
    };

    return (
        <>
            <script src="https://accounts.google.com/gsi/client" async></script>
            <Text size="xl" fw={500} mb="md">
                Sign In to Sort My Shoebox
            </Text>
            {/* Social auth is hidden for now */}
            {/* <Flex mb="md" mt="md" gap={isMobile ? 'sm': 'md'} direction={isMobile ? 'column' : 'row'}>
                <SocialAuth
                    onSuccess={(response: any) => {
                        console.log('Apple login success:', response);
                    }}
                    provider="Apple"
                    authType="Sign In"
                />
                <SocialAuth
                    onSuccess={(response: any) => {
                        console.log('Google login success:', response);
                    }}
                    provider="Google"
                    authType="Sign In"
                />
            </Flex>
            <Divider label="Or sign in with email" labelPosition="center" my="lg"/> */}

            {notification && (
                <Alert
                    title={notification.title}
                    color={getNotificationColor(notification.type)}
                    mb="lg"
                    onClose={() => setNotification(null)}
                    withCloseButton
                >
                    {notification.message}
                </Alert>
            )}

            {errorMessage && (
                <Alert
                    title="Error"
                    color="red"
                    mb="lg"
                    onClose={() => setErrorMessage(null)}
                    withCloseButton
                >
                    {errorMessage}
                </Alert>
            )}

            <form onSubmit={form.onSubmit(() => handleEmailSignIn(form.values.email, form.values.password))}>
                <Stack>
                    <TextInput
                        required
                        label="Email"
                        placeholder="name@mail.com"
                        value={form.values.email}
                        onChange={(event) => form.setFieldValue('email', event.currentTarget.value)}
                        error={form.errors.email}
                        radius="md"
                    />
                    <PasswordInput
                        required
                        label="Password"
                        placeholder="Your password"
                        value={form.values.password}
                        onChange={(event) => form.setFieldValue('password', event.currentTarget.value)}
                        error={form.errors.password}
                        radius="md"
                    />
                    <Group justify="start" mt={'xxs'}>
                        <Anchor
                            component="button"
                            type="button"
                            c="blue"
                            size="xs"
                            onClick={onForgotPasswordClick}
                        >
                            Forgot your password?
                        </Anchor>
                    </Group>
                </Stack>

                <Group justify="space-between" mt="xl">
                    <Stack gap={4}>
                        <Text size="md">
                            Don't have an account yet?{' '}
                            <Anchor
                                component="button"
                                type="button"
                                c="blue"
                                size="md"
                                onClick={onRegisterClick}
                            >
                                Sign Up
                            </Anchor>
                        </Text>
                    </Stack>
                    <Button type="submit" radius="xl" color="blue" loading={submitting}>
                        Sign In
                    </Button>
                </Group>
            </form>

        </>
    );
}

export default Login;